<template>
    <Transition name="fade" mode="out-in">
        <div v-if="products.length > 0" class="wishlist-product-list flex flex-col gap-4 px-4 py-6 lg:border lg:rounded-xs lg:border-secondary lg:px-6">
            <div class="font-semibold">
                {{ $t('wishlist.product.productsOnWishlist') }}
            </div>
            <div data-cy="wishlistItems">
                <WishlistProductListItem
                    v-for="(product, index) in products"
                    :key="product.id"
                    :product="product"
                    :is-last="index === products.length - 1"
                    :excentos-configuration="excentosConfigurations[product.id]"
                    @open-configurator="excentosModal?.open($event)"
                />
            </div>
        </div>
        <div v-else class="flex flex-col items-center gap-8 px-4 py-6 lg:px-0">
            <img
                src="/images/empty-wishlist.svg"
                :alt="$t('wishlist.product.empty')"
                data-not-lazy
                aria-hidden="true"
            >
            <div class="flex flex-col gap-8">
                <div class="text-center">
                    <div class="font-semibold">
                        {{ $t('wishlist.product.emptyText1') }}
                    </div>
                    <div class="text-base-content-subtle text-sm">
                        {{ $t('wishlist.product.emptyText2') }}
                    </div>
                </div>
                <div class="flex flex-col gap-4 items-center lg:flex-row lg:flex-wrap lg:justify-center lg:gap-2 lg:max-w-[400px]">
                    <MiscLink to="/new" class="btn btn-primary">
                        {{ $t('wishlist.product.linkNew') }}
                    </MiscLink>
                    <MiscLink to="/sale" class="btn btn-secondary">
                        {{ $t('wishlist.product.linkSale') }}
                    </MiscLink>
                    <MiscLink to="/" class="btn btn-ghost lg:mt-2">
                        {{ $t('wishlist.product.linkHome') }}
                    </MiscLink>
                </div>
            </div>
        </div>
    </Transition>
    <MiscExcentosModal ref="excentosModal" />
</template>

<script setup lang="ts">
import type { Schemas } from '#shopware'
import type { MiscExcentosModal } from '#components'

withDefaults(defineProps<{
    products: Schemas['Product'][]
    excentosConfigurations?: Record<string, string>
}>(), {
    excentosConfigurations: () => ({})
})

const excentosModal = ref<InstanceType<typeof MiscExcentosModal>>()

const { handleExcentosAddToCart, handleExcentosAddToWishlist } = useExcentos({
    onStart: () => { excentosModal.value?.setLoading(true) },
    onCartSettled: () => {
        excentosModal.value?.close()
        excentosModal.value?.setLoading(false)
    },
    // A wishlist add deliberately leaves the configurator open (A4G-3275). Dropping the
    // loading gate is what reveals it again — the gate's overlay is opaque, and unlike the
    // cart path there is no `close()` here to hide it behind.
    onWishlistSettled: () => { excentosModal.value?.setLoading(false) },
})

onMounted(() => {
    window.addEventListener('dataLayerPush', handleExcentosAddToCart)
    window.addEventListener('dataLayerPush', handleExcentosAddToWishlist)
})

onBeforeUnmount(() => {
    window.removeEventListener('dataLayerPush', handleExcentosAddToCart)
    window.removeEventListener('dataLayerPush', handleExcentosAddToWishlist)
})
</script>
